import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StatsService from '../services/StatsService';
import { LeaderboardData, LeaderboardPlayer } from '../models/PlayerStats';
import '../styles/components/stats-page.scss';
import PageHeader from '../components/PageHeader';

interface StatConfig {
  key: string;
  title: string;
  description: string;
  formatter: (value: number) => string;
  statField: string;
}

const LeaderboardPage: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  
  const [seasons, setSeasons] = useState<number[]>([]);
  const [latestSeason, setLatestSeason] = useState<number | null>(null);
  const [oldestSeason, setOldestSeason] = useState<number | null>(null);

  const statConfigs: StatConfig[] = useMemo(() => [
    {
      key: 'KD',
      title: 'K/D Ratio',
      description: 'Kill/Death Ratio',
      formatter: (value) => value.toFixed(2),
      statField: 'kd'
    },
    {
      key: 'hltv2Rating',
      title: 'Rating 2.0',
      description: '2.0 Rating',
      formatter: (value) => value.toFixed(2),
      statField: 'hltv2Rating'
    },
    {
      key: 'ADR',
      title: 'ADR',
      description: 'Average Damage per Round',
      formatter: (value) => value.toFixed(2),
      statField: 'adr'
    },
    {
      key: 'HS',
      title: 'HS%',
      description: 'Headshot Percentage',
      formatter: (value) => `${value.toFixed(2)}%`,
      statField: 'hs'
    },
    {
      key: 'KAST',
      title: 'KAST%',
      description: 'Kill, Assist, Survival, Trade %',
      formatter: (value) => `${value.toFixed(2)}%`,
      statField: 'kast'
    },
    {
      key: 'openingDuelWinPercentage',
      title: 'Opening Duels',
      description: 'Opening Duel Win %',
      formatter: (value) => `${value.toFixed(2)}%`,
      statField: 'openingDuelWinPercentage'
    }
  ], []);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const [latest, oldest] = await Promise.all([
          StatsService.getLatestSeason(),
          StatsService.getOldestSeason()
        ]);
        
        setLatestSeason(latest);
        setOldestSeason(oldest);
        setSelectedSeason(latest);
        
        const seasonList = [];
        for (let i = latest; i >= oldest; i--) {
          seasonList.push(i);
        }
        setSeasons(seasonList);
      } catch (err) {
        console.error('Error fetching seasons:', err);
      }
    };
    
    fetchSeasons();
  }, []);


  const fetchLeaderboard = useCallback(async () => {
    if (!selectedSeason) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await StatsService.getLeaderboard(
        selectedSeason,
        user?.steamID
      );
      setLeaderboardData(data);
    } catch (err) {
      setError('Failed to load leaderboard data');
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, user?.steamID]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);


  const renderStatCard = (stat: StatConfig) => {
    const players = leaderboardData?.[stat.key] || [];

    // Debug first player structure
    if (players.length > 0 && stat.key === 'hltv2Rating') {
    }

    return (
      <div key={stat.key} className="leaderboard-stat-card">
        <div className="stat-table">
          <table>
            <thead>
              <tr>
                <th className="rank-col">#</th>
                <th className="player-col">{stat.description}</th>
                <th className="value-col">{stat.title}</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player: LeaderboardPlayer) => (
                <tr key={player.stats.steamID} className={player.isCurrentUser ? 'current-user' : ''}>
                  <td className="rank-col">{player.placement}</td>
                  <td className="player-col">
                    <Link to={`/profile/${player.stats.steamID}`}>
                      {player.stats.account?.playerName || player.stats.steamID}
                    </Link>
                  </td>
                  <td className="value-col">
                    {stat.formatter(player.stats[stat.statField as keyof typeof player.stats] as number)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="leaderboard-page">
      <PageHeader 
        title="PLAYER LEADERBOARD"
        subtitle="Top performing players across all divisions in the Northern Championships Series"
      >
        <div className="season-selector">
          <select
            value={selectedSeason || ''}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
          >
            {seasons.map(season => (
              <option key={season} value={season}>
                Season {season}
              </option>
            ))}
          </select>
        </div>
      </PageHeader>
      
      <div className="container">

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading leaderboard data...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && leaderboardData && (
        <div className="leaderboard-grid">
          {statConfigs.map(stat => renderStatCard(stat))}
        </div>
      )}
      </div>
    </div>
  );
};

export default LeaderboardPage;